import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import TickerSection from './TickerSection';
import LiveScoringTable from './LiveScoringTable';
import ColumnEditor from './ColumnEditor';
import { applyPresetToTickerData } from './scoring';
import { FIELD_CATALOG } from './fieldCatalog';
import { fetchTickerBundle, collectAllTickers } from './tickerDataBundle';
import {
  listPresets,
  fetchPreset,
  createPreset,
  savePreset as savePresetRemote,
  removePreset,
} from './scoringPresetStore';

/**
 * Single-page Scoring Builder. Top: tickers. Below: live spreadsheet.
 *
 * The persisted preset shape is unchanged — we still POST {name, columns,
 * aggregate} to /stock-analyzer/scoring-presets/. What changed is the
 * surface: there is no separate Library or Apply tab. The user sees their
 * tickers, columns, and live data with heatmapped scores in one view, like
 * a spreadsheet.
 *
 * Saved ticker groups (TickerGroup model) are a separate persisted entity so
 * the user can swap stock lists independently of scoring methodology.
 */
function ScoringBuilderPage() {
  // ---- Tickers + bundle ----
  const [tickers, setTickers] = useState(['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'TSLA']);
  const [tickerData, setTickerData] = useState(null);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundleError, setBundleError] = useState(null);
  const lastFetchKey = useRef(null);

  // ---- Columns + preset ----
  const [columns, setColumns] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loadedPresetId, setLoadedPresetId] = useState(null);
  const [presetName, setPresetName] = useState('Untitled');
  const [savedColumnsSnapshot, setSavedColumnsSnapshot] = useState('[]'); // for dirty check
  const [editorState, setEditorState] = useState(null); // { mode: 'add' | 'edit', column?: ... }
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [pageError, setPageError] = useState(null);

  // ---- Initial load ----
  useEffect(() => {
    listPresets()
      .then(setPresets)
      .catch((e) => setPageError(formatError(e, 'Could not load presets')));
  }, []);

  // ---- Debounced bundle fetch on ticker / benchmark change ----
  const benchmarkTickers = useMemo(() => {
    const out = new Set();
    for (const c of columns) {
      if (c.benchmarkTicker) out.add(String(c.benchmarkTicker).toUpperCase());
    }
    return Array.from(out);
  }, [columns]);

  useEffect(() => {
    const allTickers = collectAllTickers(tickers, columns);
    if (!allTickers.length) {
      setTickerData(null);
      return undefined;
    }
    const fetchKey = allTickers.slice().sort().join(',');
    if (fetchKey === lastFetchKey.current) return undefined;
    const handle = setTimeout(async () => {
      lastFetchKey.current = fetchKey;
      setBundleLoading(true);
      setBundleError(null);
      try {
        const data = await fetchTickerBundle(allTickers, { financialsYears: 5 });
        setTickerData(data);
      } catch (e) {
        setBundleError(formatError(e, 'Could not fetch ticker data'));
        setTickerData(null);
      } finally {
        setBundleLoading(false);
      }
    }, 800);
    return () => clearTimeout(handle);
  }, [tickers, benchmarkTickers, columns]);

  // ---- Score computation (instant, client-side) ----
  const results = useMemo(() => {
    if (!tickerData || !tickers.length) return [];
    if (!columns.length) {
      return tickers.map((t) => ({ ticker: t, aggregateScore: null, variance: 0, cells: {} }));
    }
    const fakePreset = { columns, aggregate: { method: 'weighted_mean' } };
    return applyPresetToTickerData(fakePreset, tickers, tickerData, FIELD_CATALOG);
  }, [tickerData, tickers, columns]);

  const isDirty = useMemo(
    () => JSON.stringify(columns) !== savedColumnsSnapshot,
    [columns, savedColumnsSnapshot],
  );

  // ---- Column ops ----

  const handleAddColumn = () => setEditorState({ mode: 'add' });

  const handleEditColumn = (col) => setEditorState({ mode: 'edit', column: col });

  const handleColumnSaved = (compiled) => {
    if (editorState?.mode === 'edit') {
      setColumns((cs) => cs.map((c) => (c.id === compiled.id ? compiled : c)));
    } else {
      setColumns((cs) => [...cs, compiled]);
    }
    setEditorState(null);
  };

  const handleColumnDeleted = () => {
    if (editorState?.mode !== 'edit') return;
    setColumns((cs) => cs.filter((c) => c.id !== editorState.column.id));
    setEditorState(null);
  };

  // ---- Preset ops ----

  const handleLoadPreset = useCallback(async (idStr) => {
    setPageError(null);
    if (!idStr) {
      setColumns([]);
      setLoadedPresetId(null);
      setPresetName('Untitled');
      setSavedColumnsSnapshot('[]');
      return;
    }
    try {
      const preset = await fetchPreset(Number(idStr));
      setLoadedPresetId(preset.id);
      setPresetName(preset.name);
      setColumns(preset.columns || []);
      setSavedColumnsSnapshot(JSON.stringify(preset.columns || []));
    } catch (e) {
      setPageError(formatError(e, 'Could not load preset'));
    }
  }, []);

  const handleSavePreset = async () => {
    setPageError(null);
    try {
      if (loadedPresetId) {
        const updated = await savePresetRemote(loadedPresetId, {
          name: presetName,
          schema_version: 1,
          columns,
          aggregate: { method: 'weighted_mean' },
        });
        setPresets((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));
        setSavedColumnsSnapshot(JSON.stringify(updated.columns || []));
      } else {
        setSaveAsName(presetName === 'Untitled' ? '' : presetName);
        setSavePromptOpen(true);
      }
    } catch (e) {
      setPageError(formatError(e, 'Could not save preset'));
    }
  };

  const handleSaveAsSubmit = async () => {
    setPageError(null);
    const name = saveAsName.trim();
    if (!name) { setPageError('Preset name required.'); return; }
    if (!columns.length) { setPageError('Add at least one column first.'); return; }
    try {
      const created = await createPreset({
        name,
        schema_version: 1,
        columns,
        aggregate: { method: 'weighted_mean' },
      });
      setPresets((ps) => [created, ...ps]);
      setLoadedPresetId(created.id);
      setPresetName(created.name);
      setSavedColumnsSnapshot(JSON.stringify(created.columns || []));
      setSavePromptOpen(false);
    } catch (e) {
      setPageError(formatError(e, 'Could not save preset'));
    }
  };

  const handleDeletePreset = async () => {
    if (!loadedPresetId) return;
    if (!window.confirm(`Delete preset "${presetName}"? This cannot be undone.`)) return;
    setPageError(null);
    try {
      await removePreset(loadedPresetId);
      setPresets((ps) => ps.filter((p) => p.id !== loadedPresetId));
      setColumns([]);
      setLoadedPresetId(null);
      setPresetName('Untitled');
      setSavedColumnsSnapshot('[]');
    } catch (e) {
      setPageError(formatError(e, 'Could not delete preset'));
    }
  };

  // ---- Render ----

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">Scoring Builder</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Build a custom scoring methodology one column at a time. Live data,
              live scores, your own checklist.
            </p>
          </div>
          <Link
            to="/stock-analyzer"
            className="text-sm text-blue-400 hover:text-blue-300 transition"
          >
            ← Back to Stock Analyzer
          </Link>
        </div>

        <TickerSection tickers={tickers} onTickersChange={setTickers} />

        {/* Preset bar */}
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-300">Methodology:</span>
            <select
              value={loadedPresetId ?? ''}
              onChange={(e) => handleLoadPreset(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white"
            >
              <option value="">— New (untitled) —</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({(p.columns || []).length} col{(p.columns || []).length === 1 ? '' : 's'})
                </option>
              ))}
            </select>
            {loadedPresetId && (
              <input
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white w-44"
              />
            )}
            {isDirty && (
              <span className="text-xs text-yellow-300">unsaved changes</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {loadedPresetId && (
              <button
                type="button"
                onClick={handleDeletePreset}
                className="px-2 py-1 text-xs rounded border border-red-700/60 text-red-300 hover:bg-red-900/30 transition"
              >Delete</button>
            )}
            <button
              type="button"
              onClick={handleSavePreset}
              disabled={!columns.length || (!isDirty && !!loadedPresetId)}
              className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loadedPresetId ? 'Save changes' : 'Save as…'}
            </button>
          </div>
        </div>

        {/* Save As prompt */}
        {savePromptOpen && (
          <div className="bg-gray-800 border border-blue-500/40 rounded p-3 space-y-2">
            <div className="text-sm text-white">Save current columns as a new methodology preset</div>
            <input
              autoFocus
              type="text"
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAsSubmit(); }}
              placeholder="Preset name (e.g. My Value Score)"
              className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSavePromptOpen(false)}
                className="px-3 py-1 text-xs rounded border border-gray-600 text-gray-200 hover:bg-gray-700 transition"
              >Cancel</button>
              <button
                type="button"
                onClick={handleSaveAsSubmit}
                className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white transition"
              >Save</button>
            </div>
          </div>
        )}

        {/* Add-column action */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleAddColumn}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white transition"
          >
            + Add column
          </button>
          <span className="text-xs text-gray-500">
            Click a column header in the table to edit it.
          </span>
          {bundleLoading && (
            <span className="text-xs text-blue-300">Loading data…</span>
          )}
          {bundleError && (
            <span className="text-xs text-red-300">{bundleError}</span>
          )}
        </div>

        {pageError && (
          <div className="bg-red-900/30 border border-red-500/60 rounded-md px-4 py-3 text-sm text-red-200 whitespace-pre-wrap">
            {pageError}
          </div>
        )}

        {/* Live table */}
        <LiveScoringTable
          columns={columns}
          results={results}
          onEditColumn={handleEditColumn}
        />

        {/* Disclaimer */}
        <div className="bg-blue-900/20 border-2 border-blue-500/50 rounded-lg p-3 text-xs text-blue-300">
          Educational tool. Scores reflect your own methodology, not investment advice.
        </div>
      </div>

      {editorState && (
        <ColumnEditor
          initialColumn={editorState.mode === 'edit' ? editorState.column : null}
          onSave={handleColumnSaved}
          onDelete={editorState.mode === 'edit' ? handleColumnDeleted : null}
          onCancel={() => setEditorState(null)}
        />
      )}
    </div>
  );
}

function formatError(err, fallback) {
  if (!err) return fallback;
  const data = err.response?.data;
  if (data && typeof data === 'object') {
    const lines = [];
    for (const [k, v] of Object.entries(data)) {
      if (Array.isArray(v)) lines.push(`${k}: ${v.join('; ')}`);
      else lines.push(`${k}: ${String(v)}`);
    }
    if (lines.length) return lines.join('\n');
  }
  return err.message || fallback;
}

export default ScoringBuilderPage;
